import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { forgotPasswordApi, verifyOtpApi, resetPasswordApi } from '../../utils/api';

const ForgotPassword = ({ onBackToLogin }) => {
  const { showToast } = useToast();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendIn]);

  const sendOtp = async () => {
    if (!email.trim()) { setError('Please enter your email'); return; }
    setLoading(true); setError('');
    const res = await forgotPasswordApi(email.trim());
    setLoading(false);
    if (!res.success) { setError(res.message || 'Failed to send OTP'); return; }
    setStep('otp');
    setResendIn(60);
    showToast(`OTP sent to ${res.data?.email || email}`, 'success');
  };

  const verifyOtp = async () => {
    if (!otp.trim() || otp.trim().length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setLoading(true); setError('');
    const res = await verifyOtpApi(email.trim(), otp.trim());
    setLoading(false);
    if (!res.success) { setError(res.message || 'Invalid OTP'); return; }
    setStep('reset');
  };

  const submitReset = async () => {
    if (newPassword.length < 4) { setError('Password must be at least 4 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    const res = await resetPasswordApi(email.trim(), otp.trim(), newPassword);
    setLoading(false);
    if (!res.success) { setError(res.message || 'Failed to reset password'); return; }
    showToast('Password reset successfully', 'success');
    setStep('done');
  };

  const resend = async () => {
    if (resendIn > 0) return;
    setLoading(true); setError('');
    const res = await forgotPasswordApi(email.trim());
    setLoading(false);
    if (!res.success) { setError(res.message || 'Failed to resend'); return; }
    setResendIn(60);
    showToast('A new OTP has been sent', 'success');
  };

  const STEPS = ['email', 'otp', 'reset'];
  const stepIdx = STEPS.indexOf(step);

  return (
    <>
      {step !== 'done' && (
        <button
          type="button"
          className="login-back-arrow"
          onClick={onBackToLogin}
          aria-label="Back to Sign In"
        >
          <ArrowLeft size={18} />
        </button>
      )}

      {step !== 'done' && (
        <div className="login-stepper" aria-label="Progress">
          {['Email', 'Verify', 'Password'].map((label, i) => (
            <React.Fragment key={label}>
              <div className={`login-stepper__dot ${i <= stepIdx ? 'is-active' : ''} ${i < stepIdx ? 'is-done' : ''}`}>
                <span className="login-stepper__num">{i + 1}</span>
                <span className="login-stepper__label">{label}</span>
              </div>
              {i < 2 && <div className={`login-stepper__line ${i < stepIdx ? 'is-active' : ''}`} />}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="login-step-anim" key={step}>
      <div className="login-header">
        <h1 className="login-title">
          {step === 'email' && 'Forgot Password'}
          {step === 'otp' && 'Verify OTP'}
          {step === 'reset' && 'Set New Password'}
          {step === 'done' && 'All Set!'}
        </h1>
        <p className="login-subtitle">
          {step === 'email' && 'Enter your email to receive a verification code'}
          {step === 'otp' && 'Enter the 6-digit code sent to your email'}
          {step === 'reset' && 'Choose a strong new password'}
          {step === 'done' && 'Your password has been reset successfully'}
        </p>
      </div>

      {error && <div className="login-error">{error}</div>}

      {step === 'email' && (
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); sendOtp(); }}>
          <div className="login-field">
            <label className="login-label">Email</label>
            <input
              className="login-input"
              type="email"
              placeholder="Enter your registered email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />
          </div>
          <button className="login-btn" type="submit" disabled={loading}>
            {loading && <Loader2 size={18} className="spin" />}
            <span>{loading ? 'Sending...' : 'Send OTP'}</span>
          </button>
        </form>
      )}

      {step === 'otp' && (
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); verifyOtp(); }}>
          <div className="login-field">
            <label className="login-label">Verification Code</label>
            <input
              className="login-input login-input--otp"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
          </div>
          <button className="login-btn" type="submit" disabled={loading}>
            {loading && <Loader2 size={18} className="spin" />}
            <span>{loading ? 'Verifying...' : 'Verify Code'}</span>
          </button>
          <div className="login-resend">
            {resendIn > 0 ? (
              <span className="login-resend__muted">
                Didn't receive it? Resend in <strong>{resendIn}s</strong>
              </span>
            ) : (
              <>
                <span className="login-resend__muted">Didn't receive the code? </span>
                <button
                  type="button"
                  className="login-resend__btn"
                  onClick={resend}
                  disabled={loading}
                >
                  Resend
                </button>
              </>
            )}
          </div>
        </form>
      )}

      {step === 'reset' && (
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); submitReset(); }}>
          <div className="login-field">
            <label className="login-label">New Password</label>
            <div className="login-password-wrap">
              <input
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                className="login-eye"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="login-field">
            <label className="login-label">Confirm Password</label>
            <input
              className="login-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </div>
          <button className="login-btn" type="submit" disabled={loading}>
            {loading && <Loader2 size={18} className="spin" />}
            <span>{loading ? 'Saving...' : 'Reset Password'}</span>
          </button>
        </form>
      )}

      {step === 'done' && (
        <div className="login-done">
          <div className="login-done__badge">
            <CheckCircle2 size={56} className="login-done__icon" />
          </div>
          <button className="login-btn login-btn--block" type="button" onClick={onBackToLogin}>
            <span>Continue to Sign In</span>
          </button>
        </div>
      )}

      {step !== 'done' && (
        <div className="login-footer-link">
          <span className="login-resend__muted">Remember your password? </span>
          <button type="button" className="login-resend__btn" onClick={onBackToLogin}>
            Sign in
          </button>
        </div>
      )}
      </div>
    </>
  );
};

export default ForgotPassword;
